import Component from '@glimmer/component'
import type { TOC } from '@ember/component/template-only'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import {
  serialize as serializeSort,
  deserialize as deserializeSort,
  updateSortColumns,
} from '@blakeelearning/data-tables/utils/sorting'
import { action } from '@ember/object'
import type { SortingConfig } from '@blakeelearning/data-tables/utils/sorting'
import { getSortViewModel } from 'teacher-ui-client/utils/sorting'
import { t } from 'ember-intl'
import { on } from '@ember/modifier'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import formatValue from 'teacher-ui-client/helpers/format-value'
import type { SpellingRuleWordsData } from 'teacher-ui-client/components/writing-legends/spelling-rule-words'

interface Signature {
  Element: HTMLTableElement
  Args: {
    isReloading?: boolean
    spellingRuleWords?: SpellingRuleWordsData
  }
}

export const SpellingRuleWordsTable: TOC<Signature> = <template>
  <table class="divide-dusty-black-200 relative table-fixed divide-y" ...attributes>
    <thead>
      <tr class="bg-white">
        <ThSort
          class="min-w-32"
          @sortKey="correct_word"
          @text={{t "components.reporting.tables.writingLegends.spelling.correctWord"}}
        />
        <ThSort
          class="min-w-32"
          @sortKey="total_correct"
          @text={{t "components.reporting.tables.writingLegends.spelling.correctFrequency"}}
        />
        <ThSort
          @sortKey="incorrect_words"
          @text={{t "components.reporting.tables.writingLegends.spelling.incorrectWord"}}
        />
        <ThSort
          class="min-w-32"
          @sortKey="total_incorrect"
          @text={{t "components.reporting.tables.writingLegends.spelling.incorrectFrequency"}}
        />
        <ThSort @sortKey="error_rate" @text={{t "components.reporting.tables.writingLegends.spelling.errorRate"}} />
      </tr>
    </thead>
    <tbody class="divide-dusty-black-200 divide-y {{if @isReloading 'opacity-50'}}">
      {{#each @spellingRuleWords.issue_words as |issue index|}}
        <tr class="bg-white" data-test-table-row="{{index}}">
          <td class="truncate p-3" data-test-cell="correct_word">
            {{issue.correct_word}}
          </td>
          <td class="truncate p-3" data-test-cell="total_correct">
            {{issue.total_correct}}
          </td>
          <td class="truncate p-3" data-test-cell="incorrect_words">
            {{issue.incorrect_words}}
          </td>
          <td class="p-3" data-test-cell="total_incorrect">
            {{issue.total_incorrect}}
          </td>
          <td class="p-3" data-test-cell="error_rate">
            {{formatValue issue.error_rate append="%"}}
          </td>
        </tr>
      {{/each}}
    </tbody>
  </table>
</template>

interface ThSortSignature {
  Element: HTMLTableCellElement
  Args: {
    text?: string
    sortKey: string
  }
}

class ThSort extends Component<ThSortSignature> {
  @queryParam('reporting/ui-scope/writing-legends/spelling') sort!: string

  get sortingConfig() {
    return deserializeSort(this.sort)
  }

  set sortingConfig(newSortConfig: SortingConfig) {
    this.sort = serializeSort(newSortConfig)
  }

  @action
  updateSort() {
    this.sortingConfig = updateSortColumns(this.sortingConfig, this.args.sortKey)
  }

  get sortViewModel() {
    return getSortViewModel(this.sortingConfig, this.args.sortKey)
  }

  <template>
    <th
      class="h-[80px] select-none"
      data-test-sort-key={{@sortKey}}
      aria-sort={{this.sortViewModel.ariaDirection}}
      ...attributes
    >
      <button
        type="button"
        class="flex h-full w-full items-center gap-x-3 p-3 text-left {{if @text 'justify-between' 'justify-end'}}"
        {{on "click" this.updateSort}}
      >
        {{#if @text}}<span class="text-dusty-black-250 font-normal uppercase">{{@text}}</span>{{/if}}
        <PhosphorIcon @icon={{this.sortViewModel.icon}} class="text-dusty-black-400 print:hidden" />
      </button>
    </th>
  </template>
}
